// src/Pages/Commerce/ImportSalesHistoryDialog.js
//
// "Migrate from sales done in my online store" — lets a merchant drag in a
// CSV export of their existing store's order history (WooCommerce's export
// is the common case) and bring it into Blitz Commerce: those orders show
// up in metrics/CommerceTransactions, and each customer gets matched to (or
// creates) a CRM client record with real purchase history, same as a live
// Blitz checkout would. See docs/cummerce/crm-commerce-mapping.md for the
// backend contract this expects — imported orders are historical records
// only and must NOT trigger live Blitz Balance crediting, since that
// revenue already settled through the merchant's original store.
//
// UI shell mirrors src/Pages/BlitzPay/t2a/csvuploadmodel.js (drag/drop →
// parse → editable preview table → confirm), swapped to order-shaped
// columns via salesImport.utils.js instead of the transfer-recipient shape.

import React, { useState, useRef, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField,
  InputAdornment, Alert, Chip,
} from "@mui/material";
import {
  Close as CloseIcon, Download as DownloadIcon, UploadFile as UploadFileIcon,
  TableChart as TableChartIcon, EditNote as EditNoteIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { parseSalesCSV, salesTemplateUrl } from "./salesImport.utils.js";
import { COMMERCE_BRAND } from "./commerceShared.js";

export default function ImportSalesHistoryDialog({ open, onClose, onImport, isSubmitting, error }) {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [editedRows, setEditedRows] = useState([]);
  const [fileName, setFileName] = useState("");

  const handleFile = useCallback((file) => {
    if (!file) return;
    if (!file.name.match(/\.csv$/i) && file.type !== "text/csv") return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const r = parseSalesCSV(e.target.result);
      setParseResult(r);
      setEditedRows(r.rows.map((row) => ({ ...row })));
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = (e) => { e.preventDefault(); setIsDragOver(false); handleFile(e.dataTransfer.files[0]); };
  const updateRowAmount = (i, val) =>
    setEditedRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, amount: val, needsAmount: false } : r)));

  const handleReset = () => { setParseResult(null); setEditedRows([]); setFileName(""); };
  const handleClose = () => { handleReset(); onClose(); };

  const handleConfirm = () => {
    const ready = editedRows.filter((r) => r.amount && parseFloat(r.amount) > 0);
    if (!ready.length) return;
    onImport(ready);
  };

  const downloadTemplate = () => {
    const url = salesTemplateUrl();
    const a = document.createElement("a");
    a.href = url; a.download = "blitz_sales_import_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const totalReady = editedRows.filter((r) => r.amount && parseFloat(r.amount) > 0).length;
  const totalMissing = editedRows.filter((r) => !r.amount || parseFloat(r.amount) <= 0).length;
  const totalNoContact = editedRows.filter((r) => r.needsContact).length;
  const grandTotal = editedRows.reduce((acc, r) => acc + (parseFloat(r.amount) || 0), 0);
  const parseErrors = parseResult?.errors || [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: 3, py: 2.25, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid", borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: alpha(COMMERCE_BRAND.sky, 0.1), display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TableChartIcon sx={{ color: COMMERCE_BRAND.skyDark, fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.2 }}>Import sales history</Typography>
              <Typography variant="caption" color="text.secondary">{fileName || "Bring in past orders from your online store"}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 13 }} />} onClick={downloadTemplate} sx={{ fontSize: "0.73rem", textTransform: "none" }}>Template</Button>
            <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Box sx={{ p: 3, pb: 0 }}>
            <Alert severity="error">{error?.message || "Import failed — try again."}</Alert>
          </Box>
        )}

        {!parseResult && (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Export your order history as a CSV from your store (most platforms, including
              WooCommerce, support this from Orders → Export) and drop it here. These orders won't
              re-charge anyone or move money — they're historical records for your CRM and reporting.
            </Typography>
            <Box
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: "2px dashed", borderColor: isDragOver ? COMMERCE_BRAND.sky : alpha(COMMERCE_BRAND.sky, 0.3),
                borderRadius: 3, p: { xs: 5, md: 7 }, textAlign: "center", cursor: "pointer", transition: "all 0.2s",
                "&:hover": { borderColor: COMMERCE_BRAND.sky, bgcolor: alpha(COMMERCE_BRAND.sky, 0.02) },
              }}
            >
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => handleFile(e.target.files[0])} />
              <UploadFileIcon sx={{ fontSize: 36, color: COMMERCE_BRAND.sky, mb: 1.5, opacity: 0.8 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.95rem" }}>{isDragOver ? "Drop it!" : "Drop CSV here"}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                or <Box component="span" sx={{ color: COMMERCE_BRAND.sky, fontWeight: 700 }}>click to browse</Box>
              </Typography>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", justifyContent: "center" }}>
                {["Order ID", "Date", "Customer Name", "Email", "Product", "Order Total", "Status"].map((c) => (
                  <Chip key={c} label={c} size="small" sx={{ fontSize: "0.68rem", height: 20, fontFamily: "monospace" }} />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {parseResult && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
              <Box sx={{ flex: 1, minWidth: 90, p: 1.5, borderRadius: 2, bgcolor: alpha("#16A34A", 0.08), border: "1px solid", borderColor: alpha("#16A34A", 0.25), textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 900, color: "#16A34A", lineHeight: 1 }}>{totalReady}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#15803D" }}>Ready</Typography>
              </Box>
              {totalMissing > 0 && (
                <Box sx={{ flex: 1, minWidth: 90, p: 1.5, borderRadius: 2, bgcolor: "warning.50", border: "1px solid", borderColor: "warning.200", textAlign: "center" }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: "warning.main", lineHeight: 1 }}>{totalMissing}</Typography>
                  <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 700 }}>Need amount</Typography>
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 90, p: 1.5, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "grey.200", textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1 }}>${grandTotal.toFixed(2)}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Total revenue</Typography>
              </Box>
            </Box>

            {parseErrors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                {parseErrors.slice(0, 4).map((e, i) => <Typography key={i} variant="caption" sx={{ display: "block" }}>• {e}</Typography>)}
                {parseErrors.length > 4 && (
                  <Typography variant="caption" sx={{ display: "block" }}>…and {parseErrors.length - 4} more rows skipped.</Typography>
                )}
              </Alert>
            )}
            {totalNoContact > 0 && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                {totalNoContact} row{totalNoContact !== 1 ? "s" : ""} {totalNoContact !== 1 ? "have" : "has"} no email — those
                orders will import but won't be matched to (or create) a CRM client record.
              </Alert>
            )}
            {totalMissing > 0 && <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}><Typography variant="body2" sx={{ fontWeight: 700 }}>Fill in missing amounts below.</Typography></Alert>}

            {editedRows.length > 0 && (
              <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {["Order", "Customer", "Product", "Date", "Amount"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.06em", bgcolor: "grey.50", color: "text.secondary" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editedRows.map((row, i) => (
                      <TableRow key={i} sx={{ bgcolor: !row.amount || parseFloat(row.amount) <= 0 ? "warning.50" : "transparent" }}>
                        <TableCell sx={{ fontSize: "0.75rem", color: "text.secondary" }}>{row.external_order_id || "—"}</TableCell>
                        <TableCell sx={{ maxWidth: 160 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.8rem" }} noWrap>{row.customer_name}</Typography>
                          {row.customer_email && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>{row.customer_email}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.78rem", color: "text.secondary", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.product_name}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", color: "text.secondary", whiteSpace: "nowrap" }}>{row.order_date || "—"}</TableCell>
                        <TableCell sx={{ minWidth: 105 }}>
                          {!row.amount || parseFloat(row.amount) <= 0
                            ? (
                              <TextField
                                size="small" type="number" placeholder="0.00" value={row.amount}
                                onChange={(e) => updateRowAmount(i, e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Typography variant="caption" fontWeight={700}>$</Typography></InputAdornment> }}
                                sx={{ width: 95, "& input": { fontSize: "0.78rem", py: 0.5 } }}
                              />
                            ) : (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#16A34A" }}>${parseFloat(row.amount).toFixed(2)}</Typography>
                                <IconButton size="small" sx={{ p: 0.25 }} onClick={() => updateRowAmount(i, "")}><EditNoteIcon sx={{ fontSize: 12, color: "text.disabled" }} /></IconButton>
                              </Box>
                            )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Button size="small" variant="text" startIcon={<UploadFileIcon sx={{ fontSize: 13 }} />} onClick={handleReset} sx={{ fontSize: "0.75rem", textTransform: "none", color: "text.secondary" }}>
                Upload a different file
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider", gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, minWidth: 80 }}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!editedRows.length || totalReady === 0 || isSubmitting}
          endIcon={<ArrowForwardIcon />}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, minWidth: 210, bgcolor: COMMERCE_BRAND.sky, "&:hover": { bgcolor: COMMERCE_BRAND.skyDark } }}
        >
          {isSubmitting
            ? "Importing…"
            : totalMissing > 0
            ? `Import ${totalReady} ready · fix ${totalMissing} more`
            : `Import ${totalReady} order${totalReady !== 1 ? "s" : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
